import type { Site } from "@src/features/settings/data/mock-sites";
import type { Role } from "@visioni/a3s-access/schemas";

export type CameraData = Array<{ site: { id: string } }>;

export type AccessManagementData = {
  users: Array<{ id: unknown; email: string; givenName?: string; familyName?: string; phoneNumber?: string }>;
  roles: Array<{ user: unknown; role: unknown }>;
};

export const parseSiteRole = (role: Role): { siteId: string; role: "manager" | "viewer" } | null => {
  if (role.startsWith("site_manager:")) return { siteId: role.substring("site_manager:".length), role: "manager" };
  if (role.startsWith("site_user:")) return { siteId: role.substring("site_user:".length), role: "viewer" };
  return null;
};

export function buildSiteAccessList(data: AccessManagementData, cameras: CameraData | undefined): Site[] {
  const siteNameMap = new Map<string, string>();
  if (cameras) {
    for (const camera of cameras) {
      const site = camera.site as { id: string; name?: string; title?: string };
      if (!siteNameMap.has(site.id)) {
        siteNameMap.set(site.id, site.name ?? site.title ?? site.id);
      }
    }
  }

  const userMap = new Map(data.users.map((u) => [String(u.id), u]));
  // Per-site dedup by userId: if the backend emits both site_manager:X and site_user:X
  // for the same user, manager wins. Keeps callbacks unambiguous and React keys unique.
  const siteUsersMap = new Map<string, Map<string, Site["users"][number]>>();

  for (const r of data.roles) {
    const parsed = parseSiteRole(r.role as Role);
    if (!parsed || parsed.siteId === "*") continue;

    const user = userMap.get(String(r.user));
    if (!user) continue;

    if (!siteUsersMap.has(parsed.siteId)) {
      siteUsersMap.set(parsed.siteId, new Map());
    }
    const usersByIdForSite = siteUsersMap.get(parsed.siteId)!;
    const existing = usersByIdForSite.get(String(user.id));
    if (existing && existing.role === "manager") continue;
    usersByIdForSite.set(String(user.id), {
      id: String(user.id),
      name: [user.givenName, user.familyName].filter(Boolean).join(" ") || user.email,
      email: user.email,
      phoneNumber: user.phoneNumber ?? "",
      role: parsed.role,
    });
  }

  // Include sites from cameras that have no role assignments yet
  if (cameras) {
    for (const camera of cameras) {
      if (!siteUsersMap.has(camera.site.id)) {
        siteUsersMap.set(camera.site.id, new Map());
      }
    }
  }

  return Array.from(siteNameMap.entries()).map(([siteId, siteName]) => ({
    id: siteId,
    name: siteName,
    users: Array.from(siteUsersMap.get(siteId)?.values() ?? []),
  }));
}
